import React, { useEffect, useState } from "react";
import { CloseIcon, IconMinus, IconPlus } from "../CustomIcons";
import winnerIcon from "../../assets/images/winner-icon.png";
import squareIcon from "../../assets/images/icon-square.png";
import venmoIcon from "../../assets/images/icon-venmo.png";
import PayPalIcon from "../../assets/images/icon-PayPal.png";
import { LeagueFundsData, saveLeagueFunds } from "./firestoreQuery";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";

interface IPaymentHandler {
  handle: string | undefined;
  methodName: string;
  icon: string;
}

interface LeagueFundsProps {
  leagueData: {
    id: string;
    leagueSize?: number;
    leagueFunds?: {
      entryFee: number;
      isPunishmentFund: boolean;
      paymentHandle: { methodName: string; handle: string };
      totalPot: number;
      distribution: { place: string; percentage: number; amount: number }[];
    };
    leagueUrl?: string;
    teams: any;
  };
  setSelectedTab: (tab: number) => void;
}

interface Distribution {
  name: string;
  percentage: number;
}

const getPlaceName = (index: number) => {
  if (index === 0) return "1st Place";
  if (index === 1) return "2nd Place";
  if (index === 2) return "3rd Place";
  return `${index + 1}th Place`;
};

// helper to force Punishment Fund to last
const enforcePunishmentFundLast = (dist: Distribution[]) => {
  const winners = dist.filter((d) => d.name !== "Punishment Fund");
  const punishment = dist.find((d) => d.name === "Punishment Fund");
  return punishment ? [...winners, punishment] : winners;
};

// helper to rename only winners
const renameWinners = (dist: Distribution[]) => {
  const winners = dist.filter((d) => d.name !== "Punishment Fund");
  const renamed = winners.map((d, i) => ({ ...d, name: getPlaceName(i) }));
  const punishment = dist.find((d) => d.name === "Punishment Fund");
  return punishment ? [...renamed, punishment] : renamed;
};

const LeagueFunds: React.FC<LeagueFundsProps> = ({
  leagueData,
  setSelectedTab,
}) => {
  const [totalPot, setTotalPot] = useState(0);
  const [entryFee, setEntryFee] = useState<number | null>(null);
  const [isOpenPaymentHandle, setIsOpenPaymentHandle] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [paymentHandle, setPaymentHandle] = useState<IPaymentHandler | null>(
    null
  );
  const [selectedPaymentHandle, setSelectedPaymentHandle] = useState<Omit<
    IPaymentHandler,
    "icon"
  > | null>(null);
  const [distribution, setDistribution] = useState<Distribution[]>([
    { name: "1st Place", percentage: 80 },
    { name: "2nd Place", percentage: 10 },
    { name: "3rd Place", percentage: 10 },
  ]);
  const [punishmentFund, setPunishmentFund] = useState(false);
  const [message, setMessage] = useState({ data: "", class: "" });
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const navigate = useNavigate();
  const { user } = useAuth();

  // 🔹 Pre-populate on edit
  useEffect(() => {
    if (leagueData?.leagueFunds) {
      const { entryFee, isPunishmentFund, paymentHandle, distribution } =
        leagueData.leagueFunds;
      setEntryFee(entryFee);
      if (paymentHandle) {
        setSelectedPaymentHandle(paymentHandle);
        setPaymentHandle({
          ...paymentHandle,
          icon: getIcon(paymentHandle.methodName),
        });
      }

      if (distribution) {
        const mapped = distribution.map((d) => ({
          name: d.place,
          percentage: d.percentage,
        }));

        setDistribution(mapped);
        setPunishmentFund(isPunishmentFund);
      }

      // Mark initial load as complete
      setIsInitialLoad(false);
    } else {
      // If no existing data, also mark initial load as complete
      setIsInitialLoad(false);
    }
  }, [leagueData]);

  const getIcon = (method: string) => {
    switch (method) {
      case "Cash App":
        return squareIcon;
      case "Venmo":
        return venmoIcon;
      case "PayPal":
        return PayPalIcon;
      default:
        return "";
    }
  };

  // 🔹 Calculate TotalPot
  useEffect(() => {
    if (entryFee && entryFee > 0 && leagueData?.leagueSize) {
      setTotalPot(entryFee * +leagueData?.leagueSize);
    } else {
      setTotalPot(0);
    }
  }, [entryFee, leagueData?.leagueSize]);

  // Only allow integer input > 0 for entry fee
  const handleOnEntryFeeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Remove all non-digit characters
    let val = e.target.value.replace(/[^\d]/g, "");
    // Restrict to max 12 digits (max value 999999999999)
    if (val.length > 12) val = val.slice(0, 12);
    const maxVal = 999999999999;
    let num = val === "" ? null : Number(val);
    if (val !== "" && num !== null && num > maxVal) {
      num = maxVal;
      val = String(maxVal);
    }
    if (val === "") {
      setEntryFee(null);
    } else if (
      num !== null &&
      !isNaN(num) &&
      Number.isInteger(num) &&
      num > 0 &&
      num <= maxVal
    ) {
      setEntryFee(num);
    } else {
      setEntryFee(null);
    }
  };

  const addPaymentHandler = ({
    method,
    icon,
  }: {
    method: string;
    icon: string;
  }) => {
    setIsOpenPaymentHandle(true);
    if (selectedPaymentHandle?.methodName === method) {
      setPaymentHandle({
        methodName: method,
        handle: selectedPaymentHandle.handle,
        icon,
      });
    } else {
      setPaymentHandle({ methodName: method, handle: undefined, icon });
    }
  };

  const handleRemove = (index: number) => {
    const winners = distribution.filter((d) => d.name !== "Punishment Fund");
    if (index === 0 || index >= winners.length) return; // can't remove 1st place or punishment
    const newWinners = winners.filter((_, i) => i !== index);
    setDistribution(
      renameWinners([
        ...newWinners,
        ...distribution.filter((d) => d.name === "Punishment Fund"),
      ])
    );
  };

  const handleAdd = () => {
    const winners = distribution.filter((d) => d.name !== "Punishment Fund");
    if (winners.length >= 5) return; // max 5 winners
    const newWinners = [
      ...winners,
      { name: getPlaceName(winners.length), percentage: 5 },
    ];
    setDistribution(
      renameWinners([
        ...newWinners,
        ...distribution.filter((d) => d.name === "Punishment Fund"),
      ])
    );
  };

  const handlePercentageChange = (index: number, value: number) => {
    setDistribution((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], percentage: value };
      return updated;
    });
  };

  const totalPercentage = distribution.reduce(
    (sum, d) => sum + d.percentage,
    0
  );

  // 🔹 Punishment Fund toggle - only runs after initial load is complete
  useEffect(() => {
    // Skip during initial load to prevent stripping punishment fund data
    if (isInitialLoad) return;

    if (punishmentFund) {
      console.log("Adding punishment fund");
      setDistribution((prev) => {
        const existing = prev.find((d) => d.name === "Punishment Fund");
        if (existing) {
          return enforcePunishmentFundLast(prev);
        }
        return enforcePunishmentFundLast([
          ...prev,
          { name: "Punishment Fund", percentage: 5 },
        ]);
      });
    } else {
      console.log("Removing punishment fund");
      setDistribution((prev) =>
        prev.filter((d) => d.name !== "Punishment Fund")
      );
    }
  }, [punishmentFund, isInitialLoad]);

  useEffect(() => {
    if (totalPercentage !== 100) {
      setMessage({
        data: "Check your math, stakes must equal 100%",
        class: "errorTxt",
      });
    } else {
      setMessage({ data: "100% of the funds are being used", class: "" });
    }
  }, [distribution]);

  const isFormValid =
    entryFee &&
    entryFee > 0 &&
    totalPercentage === 100 &&
    selectedPaymentHandle;

  const handleSave = async () => {
    if (!leagueData?.id) return;
    setIsSaving(true);
    const saveData = {
      entryFee,
      isPunishmentFund: punishmentFund,
      paymentHandle: selectedPaymentHandle,
      totalPot,
      distribution: distribution.map((d) => ({
        place: d.name,
        percentage: d.percentage,
        amount: (totalPot * d.percentage) / 100,
      })),
    };
    console.log(saveData);
    let team = {};
    if (!(leagueData.teams && leagueData?.teams.length > 0)) {
      team = {
        createdBy: user?.uid,
        name: "Commissioner",
        joinedAt: new Date().toISOString(),
        isDuePaid: false,
      };
    }
    try {
      await saveLeagueFunds(leagueData.id, saveData as LeagueFundsData, team);
    } catch (error) {
      console.log(error);
    }
    setIsSaving(false);
    setSelectedTab(2);
  };

  return (
    <>
      <div className="pageHeading">
        <span>League Funds</span>
        <span
          className="closeIcon"
          onClick={() => {
            navigate(`/${leagueData.leagueUrl}`);
          }}
        >
          <CloseIcon />
        </span>
      </div>

      {/* Entry Fee */}
      <div className="formGroup">
        <label>
          Entry Fee <span className="reqTxt">Required</span>
        </label>
        <div className="fldHavePrefix">
          <span className="fldPrefix">$</span>
          <input
            className="fldStyle"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={entryFee ?? undefined}
            defaultValue={entryFee ?? undefined}
            maxLength={12}
            onChange={handleOnEntryFeeChange}
          />
        </div>
        <div className="paymentMode">
          <ul>
            <li
              onClick={() =>
                addPaymentHandler({ method: "Cash App", icon: squareIcon })
              }
            >
              <img src={squareIcon} alt="" />
              {selectedPaymentHandle?.methodName === "Cash App" && (
                <span>{selectedPaymentHandle.handle}</span>
              )}
            </li>
            <li
              onClick={() =>
                addPaymentHandler({ method: "Venmo", icon: venmoIcon })
              }
            >
              <img src={venmoIcon} alt="" />
              {selectedPaymentHandle?.methodName === "Venmo" && (
                <span>{selectedPaymentHandle.handle}</span>
              )}
            </li>
            <li
              onClick={() =>
                addPaymentHandler({ method: "PayPal", icon: PayPalIcon })
              }
            >
              <img src={PayPalIcon} alt="" />
              {selectedPaymentHandle?.methodName === "PayPal" && (
                <span>{selectedPaymentHandle.handle}</span>
              )}
            </li>
          </ul>
        </div>
      </div>

      {/* Punishment Fund toggle */}
      <div className="formGroup">
        <label>
          Is there a Punishment Fund? <span className="reqTxt">Required</span>
        </label>
        <div className="btnWrapInline">
          <button
            className={`btnStyle ${punishmentFund ? "btnHighlighted" : ""}`}
            onClick={() => setPunishmentFund(true)}
          >
            YES
          </button>
          <button
            className={`btnStyle ${!punishmentFund ? "btnHighlighted" : ""}`}
            onClick={() => setPunishmentFund(false)}
          >
            NO
          </button>
        </div>
      </div>

      {/* Total Pot */}
      <div className="sectionBlk">
        <div className="headingItem">
          Total Pot: <em>${totalPot.toLocaleString()}</em>
        </div>
      </div>

      {/* Distribution Rows */}
      <div className="potContentWrap">
        {distribution.map((d, index) => (
          <div className="potItemRow formGroup" key={index}>
            <div className="iconCol">
              {index === 0 ? (
                <img src={winnerIcon} alt="" />
              ) : d.name === "Punishment Fund" ? null : (
                <button onClick={() => handleRemove(index)}>
                  <IconMinus />
                </button>
              )}
            </div>
            <div className="positionCol">
              <span className="positionTxt">{d.name}</span>
              <span
                className={`subTxt ${
                  d.name === "Punishment Fund" && "payMoney"
                }`}
              >
                {`${d.name === "Punishment Fund" ? "- " : ""}$${(
                  (totalPot * d.percentage) /
                  100
                )
                  .toFixed(2)
                  .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`}
              </span>
            </div>
            <select
              id={d.name}
              className="fldStyle"
              value={d.percentage}
              onChange={(e) => handlePercentageChange(index, +e.target.value)}
            >
              {[
                5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80,
                85, 90, 95, 100,
              ].map((p) => (
                <option key={p} value={p}>
                  {p}%
                </option>
              ))}
            </select>
          </div>
        ))}

        {/* Add button */}
        {distribution.filter((d) => d.name !== "Punishment Fund").length <
          5 && (
          <div className="potItemRow formGroup">
            <div className="iconCol">
              <button onClick={handleAdd}>
                <IconPlus />
              </button>
            </div>
            <div className="positionCol cursorPointer">
              <span
                className="positionTxt"
                onClick={handleAdd}
                style={{ cursor: "pointer" }}
              >
                Add
              </span>
            </div>
          </div>
        )}

        <p className={`noteTxt ${message.class}`}>{message.data}</p>
      </div>

      <button
        className="btnStyle bigBtn floatingBtn"
        disabled={!isFormValid || isSaving}
        onClick={handleSave}
      >
        CONTINUE
      </button>

      {/* Payment Handle Modal */}
      {isOpenPaymentHandle && (
        <div className="popupWrapper">
          <div className="modalPaymentHandle">
            <div className="popupHeading">
              Enter {paymentHandle?.methodName} Handle
            </div>
            <div className="paymentModePopup">
              <img src={paymentHandle?.icon} alt="" />
              <input
                placeholder="Enter handle"
                defaultValue={paymentHandle?.handle}
                maxLength={30}
                onChange={(e) =>
                  setPaymentHandle((prev) =>
                    prev ? { ...prev, handle: e.target.value } : null
                  )
                }
              />
            </div>
            <div className="btnWrap">
              <button
                className="btnStyle"
                onClick={() => setIsOpenPaymentHandle(false)}
              >
                CANCEL
              </button>
              <button
                className="btnStyle btnHighlighted"
                onClick={() => {
                  if (
                    paymentHandle &&
                    paymentHandle.handle &&
                    paymentHandle.handle.trim().length > 0
                  ) {
                    setSelectedPaymentHandle({
                      methodName: paymentHandle?.methodName,
                      handle: paymentHandle?.handle,
                    });
                  }
                  setIsOpenPaymentHandle(false);
                }}
              >
                SAVE
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LeagueFunds;
